/**
 * EPIC-DM01-G: Public Shared Swing Page
 * Displays shared swing analysis to public viewers (no auth required)
 * 
 * Route: /share/:token
 * 
 * Note: This page acts as a fallback/redirect. The edge function returns
 * full HTML directly for better SEO and social previews. This React component
 * is for client-side navigation and progressive enhancement.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShareCard } from '@/components/demo/ShareCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, Link2Off } from 'lucide-react';

interface ShareData {
  insight: string;
  focusArea: string;
  overallScore?: number;
  format: '1:1' | '9:16';
}

export default function SharedSwing() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchShareData() {
      if (!token) {
        setError('Invalid share link');
        setIsLoading(false);
        return;
      }

      try {
        // The edge function returns HTML directly, but we can also fetch JSON
        // by checking content-type or using a query param
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/share?token=${encodeURIComponent(token)}&format=json`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('expired');
          } else {
            setError('Failed to load share data');
          }
          setIsLoading(false);
          return;
        }

        // Check if we got JSON or HTML
        const contentType = response.headers.get('content-type');
        
        if (contentType?.includes('application/json')) {
          const data = await response.json();
          setShareData({
            insight: data.insight || 'Your swing has been analyzed!',
            focusArea: data.focusArea || 'Swing Improvement',
            overallScore: data.overallScore,
            format: data.format || '1:1',
          });
        } else {
          // Got HTML response - the edge function handles rendering
          // For client-side nav, we'll redirect to let edge function handle it
          window.location.href = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/share?token=${encodeURIComponent(token)}`;
          return;
        }
      } catch (err) {
        console.error('[SharedSwing] Error:', err);
        setError('Failed to load share data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchShareData();
  }, [token]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Error/Expired state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Link2Off className="h-16 w-16 mx-auto mb-6 text-slate-500" />
          <h1 className="text-2xl font-bold text-white mb-3">
            {error === 'expired' ? 'Share Link Expired' : 'Link Not Found'}
          </h1>
          <p className="text-slate-400 mb-8">
            {error === 'expired' 
              ? 'This share link is no longer available. It may have expired or been revoked by the owner.'
              : 'The share link you followed is invalid or has been removed.'}
          </p>
          <Button
            onClick={() => window.location.href = import.meta.env.VITE_APP_URL || '/'}
            className="bg-primary hover:bg-primary/90"
          >
            Get Your Swing Analyzed
          </Button>
          <p className="text-xs text-slate-500 mt-6">
            Golf Swing Studio
          </p>
        </div>
      </div>
    );
  }

  // Success state with share data
  if (shareData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-8 max-w-md">
          {/* Share Card */}
          <div className="w-72">
            <ShareCard
              format={shareData.format}
              insight={shareData.insight}
              focusArea={shareData.focusArea}
              overallScore={shareData.overallScore}
            />
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              size="lg"
              onClick={() => window.location.href = import.meta.env.VITE_APP_URL || '/'}
              className="bg-primary hover:bg-primary/90 h-14 px-8"
            >
              Get Your Swing Analyzed
            </Button>
            <p className="text-xs text-slate-500 mt-4">
              AI-Powered Swing Analysis
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return null;
}
